import { DateOnly } from '@if/core/date'
import { formatDate } from '@if/core/utils'
import { ArrowPopover, Icon, useMenu } from '@if/mui'
import { Chip } from '@mui/material'
import { DateCalendar } from '@mui/x-date-pickers'
import { setSimulatedDate, useSimulatedDate } from '@src/services/DateTimeProvider'
import { isProd } from '@src/types/constants'
import { CalendarDays, X } from 'lucide-react'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Test tool, app-wide: lets the agent drive every screen as if today were another day.
 * Neutral while the real date is in effect, warning-coloured once pinned. Hidden in prod,
 * where the API ignores the header anyway. Changing the date reloads the page so every
 * screen recomputes under the new day; pending local edits are lost.
 */
const SimulatedDateChip = () => {
  const { t } = useTranslation()
  const simulatedDate = useSimulatedDate()
  const [chipProps, popoverProps] = useMenu()

  // A full reload rather than a query invalidation: every "today" the screens hold (memoised
  // dates, form defaults, derived state) is rebuilt from scratch under the new day, and the
  // date itself survives the reload in sessionStorage.
  const applyDate = useCallback((date: DateOnly | null) => {
    setSimulatedDate(date)
    window.location.reload()
  }, [])

  if (isProd) return null

  // Derived from the hook's value, not from getToday(): one storage read per render, and the
  // label can never disagree with the chip's colour. This is the one spot where the real
  // date is legitimately shown as the fallback.
  // eslint-disable-next-line no-restricted-syntax -- the chip is the override's own UI
  const today = simulatedDate ?? DateOnly.today()

  return (
    <>
      <Chip
        {...chipProps}
        icon={<Icon icon={CalendarDays} size="sm" />}
        color={simulatedDate ? 'warning' : 'default'}
        variant={simulatedDate ? 'filled' : 'outlined'}
        // The app bar is dark in both modes; the neutral chip must take its contrast colour
        // rather than `text.primary`, which is near-black in light mode.
        sx={
          simulatedDate
            ? undefined
            : { color: 'inherit', borderColor: 'currentColor', '& .MuiChip-icon': { color: 'inherit' } }
        }
        label={`${t('menus.simulatedDate.label')} ${formatDate(today)}`}
        clickable
        onDelete={simulatedDate ? () => applyDate(null) : undefined}
        deleteIcon={<Icon icon={X} size="sm" aria-label={t('menus.simulatedDate.reset')} />}
        aria-label={t('menus.simulatedDate.pick')}
      />
      <ArrowPopover {...popoverProps}>
        <DateCalendar
          value={today.toDate()}
          onChange={(value: Date | null) => applyDate(value ? DateOnly.fromDate(value) : null)}
        />
      </ArrowPopover>
    </>
  )
}

export default SimulatedDateChip
